import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useLogout,
  useMe,
  usePartnerInvitePreview,
  usePartnersAcceptInvite,
  usePartnersReadMe,
} from '@/api/hooks';
import AuroraBackdrop from '@/components/AuroraBackdrop';
import Button from '@/components/Button';
import LoadErrorState from '@/components/LoadErrorState';
import AuthCardContainer from '@/domain/auth/components/AuthCardContainer';
import InviteSkeleton from '@/domain/invite/components/InviteSkeleton';
import InviteSummary from '@/domain/invite/components/InviteSummary';
import { type InviteView } from '@/domain/invite/types';
import {
  buildInviteAuthSearch,
  clearPendingInvite,
  getAcceptErrorView,
  getInviteView,
  savePendingInvite,
} from '@/domain/invite/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';
import { normalizeApiError } from '@/utils/error';

const InviteContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: { padding: theme.spacing(4) },
}));

/**
 * `/invite/:token`, the link of the partner invite e-mails (claim and member). Signed out it shows
 * the invite and sends the person to sign in or sign up with the invited e-mail, keeping the token;
 * signed in it accepts, or explains why this session cannot.
 */
const PartnerInvite = () => {
  const t = useTranslate('domain.invite.PartnerInvite');
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const preview = usePartnerInvitePreview(token);
  const { data: user, isLoading: isUserLoading } = useMe();
  const account = usePartnersReadMe({ enabled: Boolean(user) });
  const { mutateAsync: accept, isPending: isAccepting } = usePartnersAcceptInvite();
  const { mutateAsync: logout, isPending: isLoggingOut } = useLogout();
  const [acceptErrorView, setAcceptErrorView] = useState<InviteView | null>(null);
  const [acceptFailed, setAcceptFailed] = useState(false);

  const previewStatus = preview.error ? normalizeApiError(preview.error).status : undefined;
  const invite = preview.data;
  const isLoading = preview.isLoading || isUserLoading || (Boolean(user) && account.isLoading);

  let view: InviteView | null = null;
  if (previewStatus === 404) view = 'not_found';
  else if (invite && !isLoading) {
    view = acceptErrorView ?? getInviteView({ invite, user, account: account.data });
  }

  useEffect(() => {
    if (view === 'signed_out') savePendingInvite(token);
    if (view === 'not_found' || view === 'has_account') clearPendingInvite();
  }, [view, token]);

  const authSearch = invite ? buildInviteAuthSearch(token, invite.email) : '';

  const handleAccept = async () => {
    setAcceptFailed(false);
    try {
      await accept(token);
      clearPendingInvite();
      enqueueSnackbar(t('accepted'), { variant: 'success' });
      navigate(ScreenRoutes.HOME, { replace: true });
    } catch (error) {
      const errorView = getAcceptErrorView(normalizeApiError(error).status);
      if (errorView) setAcceptErrorView(errorView);
      else setAcceptFailed(true);
    }
  };

  // Keeps the token: the sign-in screen brings the person back here with the right e-mail.
  const handleSwitchAccount = async () => {
    try {
      await logout();
    } finally {
      savePendingInvite(token);
      navigate(`${ScreenRoutes.SIGNIN}${authSearch}`, { replace: true });
    }
  };

  const renderBody = () => {
    if (isLoading && view !== 'not_found') return <InviteSkeleton />;
    if (view === 'not_found') {
      return (
        <Stack gap={2} textAlign="center">
          <Typography component="h1" variant="h4">
            {t('notFound.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('notFound.text')}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate(user ? ScreenRoutes.HOME : ScreenRoutes.SIGNIN)}
          >
            {t(user ? 'goToPortal' : 'signIn')}
          </Button>
        </Stack>
      );
    }
    if (!invite || !view) {
      return <LoadErrorState message={t('loadError')} onRetry={() => preview.refetch()} />;
    }

    return (
      <Stack gap={3}>
        <InviteSummary invite={invite} />
        {view === 'signed_out' ? (
          <Stack gap={1.5}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t('signedOut.hint')}
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate(`${ScreenRoutes.SIGNIN}${authSearch}`)}
            >
              {t('signIn')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              fullWidth
              onClick={() => navigate(`${ScreenRoutes.SIGNUP}${authSearch}`)}
            >
              {t('signedOut.signUp')}
            </Button>
          </Stack>
        ) : null}
        {view === 'wrong_email' ? (
          <Stack gap={1.5}>
            <Alert severity="warning">
              <AlertTitle>{t('wrongEmail.title')}</AlertTitle>
              {t('wrongEmail.text', { email: user?.email ?? '', inviteEmail: invite.email })}
            </Alert>
            <Button
              variant="contained"
              fullWidth
              loading={isLoggingOut}
              onClick={handleSwitchAccount}
            >
              {t('switchAccount')}
            </Button>
          </Stack>
        ) : null}
        {view === 'has_account' ? (
          <Stack gap={1.5}>
            <Alert severity="info">
              <AlertTitle>{t('hasAccount.title')}</AlertTitle>
              {t('hasAccount.text')}
            </Alert>
            <Button variant="contained" fullWidth onClick={() => navigate(ScreenRoutes.HOME)}>
              {t('goToPortal')}
            </Button>
            <Button variant="text" fullWidth loading={isLoggingOut} onClick={handleSwitchAccount}>
              {t('switchAccount')}
            </Button>
          </Stack>
        ) : null}
        {view === 'ready' ? (
          <Stack gap={1.5}>
            {acceptFailed ? <Alert severity="error">{t('acceptError')}</Alert> : null}
            <Button
              variant="contained"
              size="large"
              fullWidth
              loading={isAccepting}
              onClick={handleAccept}
            >
              {t('accept')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    );
  };

  return (
    <InviteContainer direction="column" justifyContent="center">
      <AuroraBackdrop />
      <AuthCardContainer>{renderBody()}</AuthCardContainer>
    </InviteContainer>
  );
};

export default PartnerInvite;
